import {
  useGetFormByAssignmentId,
  useGetFormDetail2,
  useGetFormalityRequestDetail,
} from "@/queries/formaly-request";
import {
  useSaveDraftForm,
  useSaveForm,
  useUploadFormField,
  invalidateInternalFormQueries,
} from "@/queries/formaly-request/intern-forms.api";
import type {
  Section,
  Step,
  Field as FieldFormality,
} from "@/types/entities/formality-request";
import {
  createDataByIdFetcher,
  createDataCenterFetcher,
  createInteroperabilityFetcher,
} from "@/workflow_api/services/nodes.api";
import {
  Avatar,
  AvatarFallback,
  Heading,
  Icon,
  useToast,
} from "@elaniintc/goes-design-system";
import {
  ExternalDataProvider,
  MultiStepForm,
  type FormValues,
} from "@sdt/render-form-engine";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { Renderify } from "renderify-js";
import { useState } from "react";
import { FormSuccessScreen } from "./form-success-screen";
import { applyDynamicDates } from "@/workflow/components/configurations/form/FormDataFormatter";

export const FormInterns = () => {
  const { showToast } = useToast();

  // Get form
  const params = useParams<{ formId: string; procedureId: string }>();
  const { data, isFetching, isLoading } = useGetFormDetail2(
    params.formId!,
    params.procedureId!,
  );

  const { data: formByAssignmentId, isLoading: isLoadingFormByAssignment } =
    useGetFormByAssignmentId(
      data?.user_form_assignments?.[data?.user_form_assignments?.length - 1]
        ?.id as string,
    );

  const navigate = useNavigate();

  const allForm: Step[] = data?.steps ?? [];

  function getAllFields(steps: readonly Step[]): FieldFormality[] {
    const out: FieldFormality[] = [];
    for (const step of steps) {
      const sections: readonly Section[] = step.sections ?? [];
      for (const section of sections) {
        const fields: FieldFormality[] = section.fields ?? [];
        out.push(...fields);
      }
    }
    return out;
  }

  const allFields: FieldFormality[] = getAllFields(allForm);

  const filledFieldsFromFormData = useMemo(() => {
    const map = new Map<string, FieldFormality["filled_fields"]>();
    for (const step of data?.steps ?? []) {
      for (const section of step.sections ?? []) {
        for (const field of section.fields ?? []) {
          if ((field.filled_fields ?? []).length > 0) {
            map.set(field.name, field.filled_fields);
          }
        }
      }
    }
    return map;
  }, [data?.steps]);

  function prepopulateSteps(steps: Step[]): Step[] {
    return steps.map((step) => ({
      ...step,
      sections: (step.sections ?? []).map((section) => ({
        ...section,
        fields: (section.fields ?? []).map((field) => {
          const rawFilledFields =
            field.type === "file"
              ? (filledFieldsFromFormData.get(field.name) ??
                field.filled_fields ??
                [])
              : (field.filled_fields ?? []).length > 0
                ? field.filled_fields
                : (filledFieldsFromFormData.get(field.name) ?? []);

          const filledFields = rawFilledFields ?? [];
          const nonNull = filledFields.filter(
            (ff) => ff.value !== null && ff.value !== undefined,
          );
          const sorted = [...nonNull].sort(
            (a, b) =>
              new Date(a.updated_at).getTime() -
              new Date(b.updated_at).getTime(),
          );
          const selected = sorted.length > 0 ? [sorted[sorted.length - 1]] : [];

          return {
            ...field,
            filled_fields: selected,
          };
        }),
      })),
    }));
  }

  const fileFieldNames = useMemo(
    () =>
      new Set(allFields.filter((f) => f.type === "file").map((f) => f.name)),
    [allFields],
  );

  // Save files
  const extractFileFields = (data: Record<string, unknown>) => {
    const result: Record<string, File[]> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (value instanceof File && !("id" in value)) {
        result[key] = [value];
        return;
      }
      if (Array.isArray(value)) {
        const files = value.filter(
          (v): v is File => v instanceof File && !("id" in v),
        );
        if (files.length > 0) {
          result[key] = files;
        }
      }
    });

    return result;
  };

  const removeFileFields = (
    data: Record<string, unknown>,
    fileFieldNames?: Set<string>,
  ) => {
    const result: Record<string, unknown> = {};

    Object.entries(data).forEach(([key, value]) => {
      if (fileFieldNames?.has(key)) return;

      if (value instanceof File) return;

      if (Array.isArray(value)) {
        if (value.some((v) => v instanceof File)) return;
        result[key] = value;
        return;
      }

      if (value && typeof value === "object") {
        result[key] = removeFileFields(value as Record<string, unknown>);
        return;
      }

      result[key] = value;
    });

    return result;
  };

  const { mutateAsync: uploadField, isPending: isUploadingFiles } =
    useUploadFormField({ skipInvalidation: true });

  // Save form
  const { mutateAsync } = useSaveForm({ skipInvalidation: true });
  const { mutateAsync: saveDraft } = useSaveDraftForm();

  const handleAutosave = useCallback(
    async (autosaveData: { assignmentId: string; fields: FormValues }) => {
      if (!autosaveData.assignmentId) return;
      try {
        const rawFields = autosaveData.fields as Record<string, unknown>;

        const dateFieldNames = new Set(
          allFields
            .filter((f) =>
              [
                "date",
                "datetime",
                "date_range",
                "datetime_range",
                "time",
              ].includes(f.type),
            )
            .map((f) => f.name),
        );

        const fields = removeFileFields(rawFields, fileFieldNames);

        const filteredFields = Object.fromEntries(
          Object.entries(fields).filter(([key, value]) => {
            if (value === null || value === undefined || value === "")
              return false;

            if (dateFieldNames.has(key)) {
              if (typeof value === "string") return value.trim().length > 0;
              if (
                typeof value === "object" &&
                "start" in (value as object) &&
                "end" in (value as object)
              ) {
                const range = value as { start: unknown; end: unknown };
                return !!range.start && !!range.end;
              }
            }

            return true;
          }),
        );

        if (Object.keys(filteredFields).length === 0) return;
        await saveDraft({
          assignmentId: autosaveData.assignmentId,
          fields: filteredFields,
        });
      } catch {
        // silently ignore autosave errors
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [saveDraft, allFields, fileFieldNames],
  );

  const uploadAllFiles = async (
    assignmentId: string,
    filesByField: Record<string, File[]>,
  ) => {
    for (const [fieldName, files] of Object.entries(filesByField)) {
      const matchedField = allFields.find((f) => f.name === fieldName);

      if (!matchedField) {
        continue;
      }

      await uploadField({
        assignmentId,
        fieldId: matchedField.id,
        files,
      });
    }
  };

  const [isSubmitted, setIsSubmitted] = useState(false);

  const onSubmit = async (
    fields: Record<string, unknown>,
    assignmentId: string,
  ) => {
    try {
      const filesByField = extractFileFields(fields as Record<string, unknown>);
      if (Object.keys(filesByField).length > 0) {
        await uploadAllFiles(assignmentId, filesByField);
      }

      await mutateAsync({
        assignmentId,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        fields: removeFileFields(fields, fileFieldNames) as any,
        // is_final_submission: true,
      });
      setIsSubmitted(true);
      // navigate(`/tramite/${params.procedureId}`);
      showToast({
        title: "Envío de Formulario",
        description: "Los datos del formulario han sido guardados exitosamente",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });
    } catch (error) {
      console.error("Error al subir/guardar:", error);
      showToast({
        title: "Error",
        description:
          "Ocurrió un error al enviar el formulario. Intenta nuevamente.",
        options: { type: "error", duration: 5000 },
      });
    }
  };

  // Data center options
  const queryClient = useQueryClient();
  const fetchExternalData = useMemo(
    () => createDataCenterFetcher(queryClient),
    [queryClient],
  );
  const fetchInteroperability = useMemo(
    () => createInteroperabilityFetcher(queryClient /*showToast*/),
    [queryClient /* showToast*/],
  );

  const fetchDataById = useMemo(() => {
    type FetchDataById = NonNullable<
      React.ComponentProps<typeof ExternalDataProvider>["fetchDataById"]
    >;
    const fetcher = createDataByIdFetcher(queryClient);
    return (async (table: string, id: string) => {
      const data = await fetcher(table, "SDT", id);
      return (data ?? { id }) as unknown;
    }) as unknown as FetchDataById;
  }, [queryClient]);

  const prepopulatedSteps = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sourceSteps = (formByAssignmentId?.data as any)?.form?.steps || [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return prepopulateSteps(sourceSteps) as any;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formByAssignmentId?.data, filledFieldsFromFormData]);

  // Get Formality Request Detail to get the code
  const { data: formalityRequest } = useGetFormalityRequestDetail(
    params.procedureId ?? "",
  );

  const lastAssignment =
    data?.user_form_assignments?.[data?.user_form_assignments?.length - 1];

  const formEngineStatus =
    lastAssignment?.status === "IN_PROGRESS"
      ? "ASSIGNED"
      : (lastAssignment?.status ?? "ASSIGNED");

  if (isSubmitted) {
    return (
      <FormSuccessScreen
        formName={data?.name ?? "Formulario"}
        requestId={formalityRequest?.code ?? ""}
        onNavigateBack={() => {
          invalidateInternalFormQueries(queryClient);
          navigate(`/tramite/${params.procedureId}`);
        }}
      />
    );
  }

  if (isLoading || isFetching || !data) {
    return (
      <div className="max-h-full h-full flex flex-col gap-6">
        <Heading variant="h3" className="mb-6">
          {data?.name}
        </Heading>
        <div className="flex flex-col items-center justify-center flex-1 gap-4">
          <Icon
            type="far"
            name="spinner-third"
            className="text-on-disabled animate-spin text-4xl"
          />
        </div>
      </div>
    );
  }

  return (
    <Renderify
      condition={!isLoading && !isFetching && !isLoadingFormByAssignment}
    >
      <div className="max-h-full h-full flex flex-col gap-6 no-portal-padding">
        <Heading
          variant="h3"
          className="mb-6 px-4 tablet:px-4 laptop:px-6 desktop:px-8"
        >
          {data?.name}
        </Heading>
        {!data?.user_form_assignments?.length &&
        !data?.form_assignments?.length ? (
          // El guard de useEffect se encarga de redirigir; esto es solo fallback
          <div className="flex flex-col items-center justify-center flex-1 gap-4">
            <Icon
              type="far"
              name="circle-exclamation"
              className="text-warning text-4xl"
            />
            <p className="text-body-quiet text-center">
              No tienes asignado este formulario
            </p>
          </div>
        ) : (
          <ExternalDataProvider
            fetchData={fetchExternalData}
            fetchInteroperability={fetchInteroperability}
            fetchDataById={fetchDataById}
          >
            <MultiStepForm
              assignmentId={lastAssignment?.id ?? ""}
              steps={applyDynamicDates(prepopulatedSteps)}
              status={formEngineStatus}
              isLoading={isLoading || isUploadingFiles}
              onAutosave={handleAutosave}
              handleSaveForm={(data) => {
                console.log(data);
                onSubmit(data.fields, data?.assignmentId);
                extractFileFields(data.fields);
              }}
              onToast={(t) => {
                showToast(t);
              }}
            />
          </ExternalDataProvider>
        )}
      </div>
    </Renderify>
  );
};
